/**
 * Services des échéances administratives.
 *
 * Lire est N1 : savoir quand expire la décennale ne fait rien sortir de
 * l'entreprise. Écrire est N2 et ouvert au patron comme à l'assistant — saisir
 * une date de TVA est un travail administratif, et le réserver au patron
 * garantirait surtout que personne ne le fasse. Le conducteur de travaux, lui,
 * n'a rien à y écrire : ce ne sont pas ses obligations.
 *
 * Rien n'est N3 : aucune de ces écritures ne sort de la base.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { AdminDeadline } from "./types.js";
import { nextOccurrence } from "./types.js";
import type {
  ListDeadlinesInput,
  RemoveDeadlineInput,
  RollDeadlineInput,
  SaveDeadlineInput,
} from "./schema.js";

export interface DeadlineRepository {
  list(ctx: ExecutionContext, includePast: boolean): Promise<AdminDeadline[]>;
  save(
    ctx: ExecutionContext,
    command: Omit<SaveDeadlineInput, "confirmationId">,
  ): Promise<AdminDeadline>;
  remove(ctx: ExecutionContext, id: string): Promise<void>;
  /** Lit une échéance seule. Sert au report, qui doit connaître son rythme. */
  read(ctx: ExecutionContext, id: string): Promise<AdminDeadline | null>;
  /** Déplace la date d'échéance, sans rien toucher d'autre. */
  moveDueDate(ctx: ExecutionContext, id: string, dueOn: string): Promise<AdminDeadline>;
}

/** Les rôles qui tiennent l'administratif de l'entreprise. */
const ADMINISTRATIF = ["patron", "assistant"] as const;

export function createDeadlineServices(repo: DeadlineRepository) {
  const listerEcheances = defineService<ListDeadlinesInput, AdminDeadline[]>({
    name: "lister_echeances",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.list(ctx, input.includePast),
  });

  const enregistrerEcheance = defineService<SaveDeadlineInput, AdminDeadline>({
    name: "enregistrer_echeance",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...ADMINISTRATIF],
    handler: (ctx, input) => {
      const { confirmationId: _ignored, ...command } = input;
      return repo.save(ctx, command);
    },
  });

  const supprimerEcheance = defineService<RemoveDeadlineInput, void>({
    name: "supprimer_echeance",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...ADMINISTRATIF],
    handler: (ctx, input) => repo.remove(ctx, input.id),
  });

  /**
   * « C'est payé, à quand la prochaine ? »
   *
   * Le geste courant, et le seul qui justifie que `recurrence` existe. Il
   * déplace la date et ne touche à rien d'autre : ni montant, ni référence, ni
   * notes. Une TVA reportée d'un mois garde son montant de la veille, ce qui
   * est faux — mais c'est un montant que le patron corrigera en le voyant,
   * alors qu'un montant effacé serait un champ vide qu'il oublierait de
   * remplir.
   */
  const reporterEcheance = defineService<RollDeadlineInput, AdminDeadline>({
    name: "reporter_echeance",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...ADMINISTRATIF],
    handler: async (ctx, input) => {
      const echeance = await repo.read(ctx, input.id);
      if (!echeance) {
        throw new DomainError("not_found", "Cette échéance n'existe pas.");
      }
      const suivante = nextOccurrence(echeance.dueOn, echeance.recurrence);
      if (suivante == null) {
        // Refusé plutôt que silencieusement ignoré : reporter une obligation
        // ponctuelle n'a pas de sens, et la déplacer d'un an « par défaut »
        // remettrait au tableau de bord une échéance qui n'existe plus.
        throw new DomainError(
          "conflict",
          "Cette échéance est ponctuelle : elle n'a pas de date suivante. " +
            "Modifiez sa date, ou supprimez-la si elle est close.",
        );
      }
      return repo.moveDueDate(ctx, input.id, suivante);
    },
  });

  return { listerEcheances, enregistrerEcheance, supprimerEcheance, reporterEcheance };
}

export type DeadlineServices = ReturnType<typeof createDeadlineServices>;
